import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowsClockwise, CheckCircle, Warning, XCircle, Pulse,
  MagnifyingGlass, ArrowSquareOut, CloudArrowDown,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const NETWORK_COLORS = {
  impact: "bg-slate-100 text-slate-700 border-slate-200",
  partnerize: "bg-indigo-50 text-indigo-700 border-indigo-200",
  skimlinks: "bg-orange-50 text-orange-700 border-orange-200",
  mrge: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cj: "bg-amber-50 text-amber-700 border-amber-200",
  pubadhub: "bg-rose-50 text-rose-700 border-rose-200",
  pubadhub_kevin: "bg-teal-50 text-teal-700 border-teal-200",
  admedia: "bg-rose-50 text-rose-700 border-rose-200",
  flexoffers: "bg-violet-50 text-violet-700 border-violet-200",
  shopnomix_kevin: "bg-pink-50 text-pink-700 border-pink-200",
  shopnomix: "bg-lime-50 text-lime-700 border-lime-200",
  pronomix: "bg-purple-50 text-purple-700 border-purple-200",
  maxbounty: "bg-cyan-50 text-cyan-700 border-cyan-200",
  cashbackhive: "bg-teal-50 text-teal-700 border-teal-200",
};

const NETWORK_LABEL_OVERRIDE = {
  pubadhub_kevin: "PubAdHub 2",
  shopnomix_kevin: "Shopnomix InMobi",
};

const CAP_MODE_NONE = "none";
const CAP_MODE_CUSTOM = "custom";
const CAP_MODE_BLOCK = "block";

function capDraftFromStored(c) {
  if (c == null || Number(c) === -1) return { mode: CAP_MODE_NONE, custom: "" };
  if (Number(c) === 0) return { mode: CAP_MODE_BLOCK, custom: "" };
  return { mode: CAP_MODE_CUSTOM, custom: String(c) };
}

function capValueFromDraft(draft) {
  const mode = draft?.mode || CAP_MODE_NONE;
  if (mode === CAP_MODE_NONE) return { ok: true, value: -1 };
  if (mode === CAP_MODE_BLOCK) return { ok: true, value: 0 };
  const n = Number.parseInt(String(draft?.custom ?? "").trim(), 10);
  if (Number.isNaN(n) || n < 1) {
    return { ok: false, error: "Enter a daily click cap of 1 or more" };
  }
  return { ok: true, value: n };
}

const STATUS_BADGES = {
  ok: { Icon: CheckCircle, cls: "text-emerald-600 bg-emerald-50", label: "Healthy" },
  failed: { Icon: XCircle, cls: "text-red-600 bg-red-50", label: "Failed" },
  running: { Icon: Pulse, cls: "text-blue-600 bg-blue-50", label: "Running" },
  never: { Icon: Warning, cls: "text-amber-600 bg-amber-50", label: "Never run" },
};

/**
 * Network Hub — replaces the wall of color-coded "Import / Refresh / Sync"
 * buttons that used to live in the AdminBrands header.
 *
 *   1. Per-network status card: brand count, last sync age, status,
 *      24h run count, "Sync now" button.
 *   2. Upstream lookup: type a brand name → if not in catalog, hit
 *      `/admin/networks/<n>/lookup-brand` to fetch + auto-import.
 *
 * Driven by GET /admin/networks/status + POST /admin/networks/{n}/sync-now.
 */
export function NetworkHubPanel({ selectedNetwork, onSelectNetwork, onCatalogChanged }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncingKey, setSyncingKey] = useState(null);
  const [lookupNetwork, setLookupNetwork] = useState(""); // network for the active lookup
  const [lookupQuery, setLookupQuery] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupResults, setLookupResults] = useState(null);
  const [schedule, setSchedule] = useState(null);  // {enabled, networks: {mrge: bool, ...}}
  const [syncingRates, setSyncingRates] = useState(false);

  // One-click sync — re-mirrors current brand-level rates onto every
  // existing passthrough publisher_brand row across every publisher.
  // Cheap (~13k row scan, ~5 writes per re-run typically), idempotent.
  const syncPassthroughRates = async () => {
    if (syncingRates) return;
    setSyncingRates(true);
    try {
      const { data } = await API.post("/admin/publisher-brands/sync-passthrough-rates", {});
      toast.success(
        `Passthrough rates synced — ${data.updated} of ${data.seen} assignments updated`
      );
      onCatalogChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sync rates");
    } finally {
      setSyncingRates(false);
    }
  };

  const [capDrafts, setCapDrafts] = useState({});
  const [savingCapKey, setSavingCapKey] = useState(null);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/admin/networks/status");
      setStatus(data);
      const drafts = {};
      for (const n of data.networks || []) {
        drafts[n.key] = capDraftFromStored(n.click_cap_per_day);
      }
      setCapDrafts(drafts);
    } catch (e) {
      toast.error("Failed to load network status");
    } finally {
      setLoading(false);
    }
  }, []);
  const fetchSchedule = useCallback(async () => {
    try {
      const { data } = await API.get("/admin/networks/rate-refresh-schedule");
      setSchedule(data);
    } catch {}
  }, []);
  useEffect(() => { fetchStatus(); fetchSchedule(); }, [fetchStatus, fetchSchedule]);

  const saveNetworkCap = async (key) => {
    const parsed = capValueFromDraft(capDrafts[key]);
    if (!parsed.ok) {
      toast.error(parsed.error);
      return;
    }
    setSavingCapKey(key);
    try {
      const { data } = await API.put(`/admin/networks/${key}/click-cap`, {
        click_cap_per_day: parsed.value,
      });
      const saved = data?.click_cap_per_day;
      setStatus((prev) => {
        if (!prev?.networks) return prev;
        return {
          ...prev,
          networks: prev.networks.map((row) =>
            row.key === key ? { ...row, click_cap_per_day: saved } : row
          ),
        };
      });
      setCapDrafts((d) => ({ ...d, [key]: capDraftFromStored(saved) }));
      if (saved === -1 || saved == null) {
        toast.success(`${key} daily click cap cleared (no ceiling)`);
      } else if (saved === 0) {
        toast.success(`${key} daily click cap set to 0 (blocked)`);
      } else {
        toast.success(`${key} daily click cap set to ${Number(saved).toLocaleString()}/day`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save click cap");
    } finally {
      setSavingCapKey(null);
    }
  };

  const toggleScheduleNetwork = async (key) => {
    if (!schedule) return;
    const next = {
      ...schedule,
      networks: { ...schedule.networks, [key]: !schedule.networks[key] },
    };
    setSchedule(next);
    try {
      await API.put("/admin/networks/rate-refresh-schedule", next);
      toast.success(`Nightly rate refresh for ${key} ${next.networks[key] ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error("Failed to save schedule");
      fetchSchedule();
    }
  };

  const toggleScheduleEnabled = async () => {
    if (!schedule) return;
    const next = { ...schedule, enabled: !schedule.enabled };
    setSchedule(next);
    try {
      await API.put("/admin/networks/rate-refresh-schedule", next);
      toast.success(`Nightly rate refresh ${next.enabled ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error("Failed to save schedule");
      fetchSchedule();
    }
  };

  const triggerSync = async (key) => {
    setSyncingKey(key);
    try {
      const opts = key === "shopnomix" ? { timeout: 600000 } : {};
      const { data } = await API.post(`/admin/networks/${key}/sync-now`, {}, opts);
      const catalog = data.catalog || {};
      const synced =
        data.synced
        || catalog.synced
        || catalog.offers_fetched
        || data.impact?.synced
        || data.actions?.synced
        || 0;
      const inserted = catalog.inserted;
      const cpa = catalog.cpa_brands;
      const cpc = catalog.cpc_brands;
      let msg = `${key} sync complete · ${synced} synced`;
      if (cpa != null && cpc != null) {
        msg += ` · ${cpa} CPA · ${cpc} CPC`;
      }
      if (inserted != null) msg += ` · ${inserted} new`;
      if (catalog.links_missing) msg += ` · ${catalog.links_missing} missing links`;
      if (data.postbacks_queued) msg += ` · ${data.postbacks_queued} postbacks queued`;
      toast.success(msg);
      fetchStatus();
      if (onCatalogChanged) onCatalogChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${key} sync failed`);
    } finally {
      setSyncingKey(null);
    }
  };

  const doLookup = async (network, q) => {
    if (!q || q.trim().length < 2) {
      toast.error("Type at least 2 characters");
      return;
    }
    setLookupBusy(true);
    setLookupResults(null);
    try {
      const { data } = await API.post(`/admin/networks/${network}/lookup-brand`, { q });
      setLookupResults(data);
      if (data.found > 0) {
        toast.success(`Found ${data.found} brand${data.found === 1 ? "" : "s"} on ${network} — imported`);
        if (onCatalogChanged) onCatalogChanged();
        fetchStatus();
      } else {
        toast.info(`No "${q}" results on ${network}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${network} lookup failed`);
    } finally {
      setLookupBusy(false);
    }
  };

  const networks = status?.networks || [];

  return (
    <div className="space-y-3" data-testid="network-hub-panel">
      {/* Per-network status grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {loading && (
          <div className="col-span-full text-sm text-slate-400 py-4 px-3 border border-slate-200 rounded-md">
            Loading network status…
          </div>
        )}
        {!loading && networks.map((n) => {
          const sb = STATUS_BADGES[n.last_sync_status] || STATUS_BADGES.never;
          const Icon = sb.Icon;
          const isSelected = selectedNetwork === n.key;
          const isSyncing = syncingKey === n.key;
          const capDraft = capDrafts[n.key] || capDraftFromStored(n.click_cap_per_day);
          const savedCap = capDraftFromStored(n.click_cap_per_day);
          const capDirty =
            capDraft.mode !== savedCap.mode
            || (capDraft.mode === CAP_MODE_CUSTOM && capDraft.custom !== savedCap.custom);
          return (
            <div
              key={n.key}
              className={`border rounded-md p-3 cursor-pointer transition-colors ${
                isSelected ? "border-[#002FA7] bg-blue-50/40 ring-1 ring-[#002FA7]/20" : "border-slate-200 hover:border-slate-300"
              }`}
              onClick={() => onSelectNetwork?.(isSelected ? "all" : n.key)}
              data-testid={`network-card-${n.key}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${NETWORK_COLORS[n.key] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                  {NETWORK_LABEL_OVERRIDE[n.key] || n.label}
                </span>
                {!n.configured && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-500" title="API credentials missing in .env">
                    not configured
                  </span>
                )}
              </div>

              <div className="text-2xl font-mono font-semibold text-slate-900">
                {n.total_brands.toLocaleString()}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                brands in catalog
                {n.key === "shopnomix" && (n.cpa_brands != null || n.cpc_brands != null) && (
                  <span className="ml-1.5 font-mono text-[10px] text-lime-700">
                    · {(n.cpa_brands || 0).toLocaleString()} CPA · {(n.cpc_brands || 0).toLocaleString()} CPC
                  </span>
                )}
                {n.rates_populated != null && n.total_brands > 0 && (
                  <span
                    className={`ml-1.5 font-mono text-[10px] ${
                      n.rates_populated / n.total_brands < 0.5 ? "text-amber-600" : "text-emerald-600"
                    }`}
                    title={`${n.rates_populated} of ${n.total_brands} have a commission rate`}
                    data-testid={`network-rates-populated-${n.key}`}
                  >
                    · {n.rates_populated.toLocaleString()} w/ rates
                  </span>
                )}
              </div>

              <div className="mt-3 flex items-center gap-1.5">
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${sb.cls}`} title={n.last_sync_error || ""}>
                  <Icon size={10} />
                  {sb.label}
                </span>
                <span className="text-[11px] text-slate-500" data-testid={`network-last-sync-${n.key}`}>
                  {n.last_sync_age || "never synced"}
                </span>
              </div>
              {n.last_sync_duration_ms != null && (
                <div className="text-[10px] text-slate-400 mt-0.5">
                  Last took {n.last_sync_duration_ms}ms · {n.sync_runs_24h} runs in 24h
                  {n.failed_24h > 0 && <span className="text-red-600 ml-1">({n.failed_24h} failed)</span>}
                </div>
              )}
              {n.last_sync_error && (
                <div className="text-[10px] text-red-600 mt-1 truncate" title={n.last_sync_error}>
                  ⚠ {n.last_sync_error}
                </div>
              )}

              <div
                className="mt-3 pt-2 border-t border-slate-100"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                data-testid={`network-click-cap-${n.key}`}
              >
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
                  Daily click cap
                </div>
                <div className="flex items-center gap-1.5">
                  <Select
                    value={capDraft.mode}
                    disabled={savingCapKey === n.key}
                    onValueChange={(mode) =>
                      setCapDrafts((d) => ({
                        ...d,
                        [n.key]: {
                          mode,
                          custom: mode === CAP_MODE_CUSTOM
                            ? (d[n.key]?.custom || savedCap.custom || "")
                            : "",
                        },
                      }))
                    }
                  >
                    <SelectTrigger
                      className="h-7 text-[11px] flex-1 min-w-0"
                      data-testid={`network-click-cap-mode-${n.key}`}
                    >
                      <SelectValue placeholder="No cap" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={CAP_MODE_NONE}>No cap</SelectItem>
                      <SelectItem value={CAP_MODE_CUSTOM}>Custom</SelectItem>
                      <SelectItem value={CAP_MODE_BLOCK}>Block (0)</SelectItem>
                    </SelectContent>
                  </Select>
                  {capDraft.mode === CAP_MODE_CUSTOM && (
                    <Input
                      type="number"
                      min={1}
                      className="h-7 w-20 shrink-0 text-xs font-mono px-2"
                      placeholder="e.g. 10000"
                      value={capDraft.custom}
                      disabled={savingCapKey === n.key}
                      onChange={(e) =>
                        setCapDrafts((d) => ({
                          ...d,
                          [n.key]: { mode: CAP_MODE_CUSTOM, custom: e.target.value },
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") saveNetworkCap(n.key);
                      }}
                      data-testid={`network-click-cap-input-${n.key}`}
                    />
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 text-[11px] px-2 shrink-0"
                    disabled={!capDirty || savingCapKey === n.key}
                    onClick={() => saveNetworkCap(n.key)}
                    data-testid={`network-click-cap-save-${n.key}`}
                  >
                    {savingCapKey === n.key ? "Saving…" : "Save"}
                  </Button>
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5">
                  Ceiling per brand per day.
                </div>
              </div>

              <div className="mt-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                {n.manual_only ? (
                  // Manual catalog partners — brands created via Manual Brand
                  // drawer. If reporting sync exists (Shopnomix Kevin MediaMax),
                  // still offer Sync report.
                  <div className="flex flex-col gap-2 w-full">
                    <span
                      className="h-7 text-[11px] text-slate-500 italic flex items-center"
                      data-testid={`network-manual-only-${n.key}`}
                    >
                      Manual catalog — use the &quot;Add brand manually&quot; drawer on the Brands page
                    </span>
                    {n.has_reporting_sync && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs w-full"
                        disabled={isSyncing || !n.configured}
                        onClick={() => triggerSync(n.key)}
                        data-testid={`network-sync-now-${n.key}`}
                      >
                        {isSyncing ? (
                          <><ArrowsClockwise size={12} className="mr-1 animate-spin" /> Syncing…</>
                        ) : (
                          <><CloudArrowDown size={12} className="mr-1" /> Sync report</>
                        )}
                      </Button>
                    )}
                  </div>
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs flex-1"
                      disabled={isSyncing || !n.configured}
                      onClick={() => triggerSync(n.key)}
                      data-testid={`network-sync-now-${n.key}`}
                    >
                      <ArrowsClockwise size={11} className={`mr-1 ${isSyncing ? "animate-spin" : ""}`} />
                      {isSyncing ? "Syncing…" : "Sync now"}
                    </Button>
                    {n.key !== "shopnomix" && n.key !== "pronomix" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        disabled={!n.configured}
                        onClick={() => { onSelectNetwork?.(n.key); setLookupNetwork(n.key); }}
                        title={`Search ${NETWORK_LABEL_OVERRIDE[n.key] || n.label}'s upstream catalog by name`}
                        data-testid={`network-lookup-open-${n.key}`}
                      >
                        <CloudArrowDown size={11} className="mr-1" /> Lookup
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Nightly auto-rate-refresh schedule — one toggle per network. mrge
          + skimlinks default ON; impact/partnerize/cj get rates via their
          action sync so they default OFF. */}
      {schedule && (
        <div
          className="border border-slate-200 rounded-md bg-white p-3"
          data-testid="rate-refresh-schedule"
        >
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-xs font-semibold text-slate-800">
                Nightly auto-rate-refresh
              </div>
              <div className="text-[11px] text-slate-500">
                Runs at the same time as the daily report. Pulls fresh rates
                for any brand still missing one — no manual clicks needed.
              </div>
            </div>
            <button
              type="button"
              className={`flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded transition-colors ${
                schedule.enabled
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                  : "bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200"
              }`}
              onClick={toggleScheduleEnabled}
              data-testid="rate-refresh-schedule-master-toggle"
            >
              <span
                className={`inline-block w-1.5 h-1.5 rounded-full ${
                  schedule.enabled ? "bg-emerald-500" : "bg-slate-400"
                }`}
              />
              {schedule.enabled ? "Enabled" : "Disabled"}
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {Object.entries(schedule.networks).map(([key, on]) => (
              <button
                key={key}
                type="button"
                disabled={!schedule.enabled}
                onClick={() => toggleScheduleNetwork(key)}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-colors border ${
                  on
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                    : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
                } ${!schedule.enabled ? "opacity-40 cursor-not-allowed" : ""}`}
                data-testid={`rate-refresh-toggle-${key}`}
              >
                <span
                  className={`inline-block w-1.5 h-1.5 rounded-full ${
                    on ? "bg-emerald-500" : "bg-slate-400"
                  }`}
                />
                {key}
              </button>
            ))}
          </div>

          {/* On-demand "re-mirror" — pushes the current brand rates onto
              every existing passthrough assignment. Useful right after
              a catalog refresh / Smart Add to clear any stale displays
              without waiting for the nightly cycle. */}
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              disabled={syncingRates}
              onClick={syncPassthroughRates}
              data-testid="sync-passthrough-rates-btn"
            >
              <ArrowsClockwise size={12} className={`mr-1 ${syncingRates ? "animate-spin" : ""}`} />
              {syncingRates ? "Syncing…" : "Sync passthrough rates now"}
            </Button>
            <span className="text-[10px] text-slate-500">
              Re-mirrors current brand rates onto every existing passthrough assignment. Safe to re-run.
            </span>
          </div>
        </div>
      )}

      {/* Inline upstream lookup row */}
      {lookupNetwork && (
        <div className="border border-slate-200 rounded-md bg-slate-50 p-3" data-testid="network-lookup-panel">
          <div className="flex items-center gap-2 mb-2">
            <CloudArrowDown size={14} className="text-[#002FA7]" />
            <span className="text-xs font-semibold text-slate-700">Search {NETWORK_LABEL_OVERRIDE[lookupNetwork] || lookupNetwork} upstream catalog</span>
            <span className="text-[10px] text-slate-400">— matched brands are auto-imported into your catalog</span>
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto h-6 text-xs text-slate-500"
              onClick={() => { setLookupNetwork(""); setLookupResults(null); setLookupQuery(""); }}
            >
              Close
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <MagnifyingGlass size={14} className="text-slate-400" />
            <Input
              autoFocus
              placeholder={`e.g. "Brooks Running", "Adidas", "Adobe"`}
              value={lookupQuery}
              onChange={(e) => setLookupQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") doLookup(lookupNetwork, lookupQuery); }}
              className="h-8 text-sm bg-white"
              data-testid="network-lookup-input"
            />
            <Button
              size="sm"
              className="h-8 text-xs bg-[#002FA7] hover:bg-[#0428A0]"
              disabled={lookupBusy || lookupQuery.trim().length < 2}
              onClick={() => doLookup(lookupNetwork, lookupQuery)}
              data-testid="network-lookup-submit"
            >
              {lookupBusy ? "Searching…" : "Search"}
            </Button>
          </div>
          {lookupResults && (
            <div className="mt-2 text-xs text-slate-600">
              {lookupResults.found === 0 ? (
                <span className="text-amber-600">No matches found on {lookupResults.network} for "{lookupResults.query}".</span>
              ) : (
                <div>
                  <span className="font-medium text-emerald-700">
                    {lookupResults.found} brand{lookupResults.found === 1 ? "" : "s"} imported:
                  </span>
                  <div className="mt-1 grid gap-1 max-h-40 overflow-auto">
                    {lookupResults.imported.slice(0, 10).map((b) => (
                      <div key={b.campaign_id} className="flex items-center gap-2 px-2 py-1 bg-white rounded border border-slate-100">
                        <span className="font-medium text-slate-900">{b.name}</span>
                        <span className="text-[10px] font-mono text-slate-400">{b.campaign_id}</span>
                        {b.base_commission_pct ? (
                          <span className="ml-auto font-mono text-[10px] text-slate-600">{b.base_commission_pct}%</span>
                        ) : null}
                        {b.advertiser_url && (
                          <a href={b.advertiser_url.startsWith("http") ? b.advertiser_url : `https://${b.advertiser_url}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5">
                            site <ArrowSquareOut size={9} />
                          </a>
                        )}
                      </div>
                    ))}
                    {lookupResults.imported.length > 10 && (
                      <div className="text-[10px] text-slate-400 px-2 py-0.5">…{lookupResults.imported.length - 10} more in catalog</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
